// apps/client/src/modules/Shortcuts/useHeldModifiers.ts
import { useEffect, useRef } from 'react'
import { atom, useStore } from 'jotai'
import { IS_MAC } from './ShortcutRegistry'

export type HeldModifiers = { cmd: boolean; shift: boolean; alt: boolean }

export const heldModifiersAtom = atom<HeldModifiers>({
  cmd: false,
  shift: false,
  alt: false,
})

const HINT_SUPPRESS_SELECTORS =
  'input, textarea, [contenteditable="true"], .cm-content, .monaco-editor, .jp-Notebook'

export const hintsSuppressedAtom = atom<boolean>(false)

const isInHintSuppressedZone = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) return false
  return !!target.closest(HINT_SUPPRESS_SELECTORS)
}

const CMD_SHOW_DEBOUNCE_MS = 80

/**
 * Tracks which modifier keys are currently held. Mount once at Workspace.tsx.
 * Resets to all-false on window blur / document hidden so badges don't get
 * stuck after an alt-tab. Also tracks whether focus is inside a hint-suppress
 * zone so consumers can render nothing while the user is typing.
 */
export const useHeldModifiers = (): void => {
  const store = useStore()
  const cmdShowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const reset = () => {
      if (cmdShowTimerRef.current) {
        clearTimeout(cmdShowTimerRef.current)
        cmdShowTimerRef.current = null
      }
      store.set(heldModifiersAtom, { cmd: false, shift: false, alt: false })
    }

    const isCmd = (e: KeyboardEvent) => IS_MAC ? e.metaKey : e.ctrlKey

    const update = (e: KeyboardEvent) => {
      const next: HeldModifiers = {
        cmd: isCmd(e),
        shift: e.shiftKey,
        alt: e.altKey,
      }
      const prev = store.get(heldModifiersAtom)
      const cmdJustPressed = next.cmd && !prev.cmd

      if (cmdJustPressed) {
        // Debounce the cmd-on flicker. shift/alt apply immediately because
        // they're typically the second key in a chord and shouldn't lag.
        store.set(heldModifiersAtom, { ...prev, shift: next.shift, alt: next.alt })
        if (cmdShowTimerRef.current) clearTimeout(cmdShowTimerRef.current)
        cmdShowTimerRef.current = setTimeout(() => {
          store.set(heldModifiersAtom, { cmd: true, shift: next.shift, alt: next.alt })
          cmdShowTimerRef.current = null
        }, CMD_SHOW_DEBOUNCE_MS)
        return
      }

      if (cmdShowTimerRef.current && !next.cmd) {
        clearTimeout(cmdShowTimerRef.current)
        cmdShowTimerRef.current = null
      }

      if (
        next.cmd !== prev.cmd ||
        next.shift !== prev.shift ||
        next.alt !== prev.alt
      ) {
        store.set(heldModifiersAtom, next)
      }
    }

    const updateSuppress = () => {
      const next = isInHintSuppressedZone(document.activeElement)
      if (store.get(hintsSuppressedAtom) !== next) {
        store.set(hintsSuppressedAtom, next)
      }
    }

    const onVisibility = () => {
      if (document.hidden) reset()
    }

    window.addEventListener('keydown', update)
    window.addEventListener('keyup', update)
    window.addEventListener('blur', reset)
    document.addEventListener('focusin', updateSuppress, true)
    document.addEventListener('focusout', updateSuppress, true)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      window.removeEventListener('keydown', update)
      window.removeEventListener('keyup', update)
      window.removeEventListener('blur', reset)
      document.removeEventListener('focusin', updateSuppress, true)
      document.removeEventListener('focusout', updateSuppress, true)
      document.removeEventListener('visibilitychange', onVisibility)
      if (cmdShowTimerRef.current) clearTimeout(cmdShowTimerRef.current)
    }
  }, [store])
}
